/**
 * Cookie consent — Pack 10.D (Privacy, GDPR & Cookie Consent Surfaces).
 *
 * Owning task: Task #1024.
 *
 * Centralised, framework-agnostic helpers for the worker- and operator-
 * facing cookie consent banner. Persists the user's per-category opt-in
 * choices in `localStorage` (storage key `nashid:cookie-consent:v1`) so
 * the decision survives across reloads without a server round-trip.
 *
 * Categories (the EU/EDPB minimum split):
 *   - "essential"   — strictly-necessary cookies (auth, csrf, session).
 *                     Always granted; cannot be opted out of.
 *   - "analytics"   — product analytics (pageview tracking, performance
 *                     metric beacons).
 *   - "marketing"   — none today; reserved so that the choice can be
 *                     persisted forward without invalidating older
 *                     consent records.
 *
 * The "v1" suffix on the storage key is the schema version. Bump it
 * whenever the categories list changes — older payloads will be ignored
 * and the banner will be re-prompted, which is the correct GDPR behaviour
 * (re-collect consent when the processing scope materially changes).
 */

export type CookieConsentCategory = "essential" | "analytics" | "marketing";

export interface CookieConsentChoice {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string; // ISO-8601 timestamp
  version: 1;
}

export const COOKIE_CONSENT_STORAGE_KEY = "nashid:cookie-consent:v1";
export const COOKIE_CONSENT_EVENT = "nashid:cookie-consent-changed";

/**
 * Browser-safe localStorage accessor. Returns `null` when running on the
 * server (SSR), in private browsing modes that throw on access, or when
 * the persisted blob fails JSON parse / shape validation. Callers MUST
 * treat `null` as "the user has not yet decided" and prompt accordingly.
 */
export function getCookieConsent(): CookieConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsentChoice> | null;
    if (
      !parsed ||
      parsed.version !== 1 ||
      parsed.essential !== true ||
      typeof parsed.analytics !== "boolean" ||
      typeof parsed.marketing !== "boolean" ||
      typeof parsed.decidedAt !== "string"
    ) {
      return null;
    }
    return parsed as CookieConsentChoice;
  } catch {
    return null;
  }
}

/** Persist a consent decision and broadcast a window event. */
export function setCookieConsent(input: {
  analytics: boolean;
  marketing: boolean;
}): CookieConsentChoice {
  const choice: CookieConsentChoice = {
    essential: true,
    analytics: input.analytics,
    marketing: input.marketing,
    decidedAt: new Date().toISOString(),
    version: 1,
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(choice));
      window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: choice }));
    } catch {
      // Private browsing or quota exhaustion — the banner will simply
      // re-prompt next page load. This is the correct GDPR fallback.
    }
  }
  return choice;
}

/** Reset / withdraw consent. The banner will re-prompt next load. */
export function clearCookieConsent(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: null }));
  } catch {
    /* noop */
  }
}

/**
 * Synchronous check used by analytics / marketing scripts before they
 * fire any beacon. Essential always returns true (the cookies are
 * required for the session to work at all).
 */
export function hasCookieConsent(category: CookieConsentCategory): boolean {
  if (category === "essential") return true;
  const choice = getCookieConsent();
  if (!choice) return false;
  return choice[category];
}
